import { LicensingAgency, MccsRoles } from '@/@types/generated'
import { HasFeature } from '@/components/Atoms/Permissions'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@/components/ui/navigation-menu'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { useAuth } from '@/hooks/useAuth'
import { LicensingAgentsFeature } from '@/lib/features'
import { Feature } from '@/lib/features/feature'
import { cn } from '@/lib/utils'
import { buildPath, RouteKey } from '@/routes'
import { PropsWithChildren } from 'react'
import { NavLink as ReactRouterNavLink, To } from 'react-router'
import { useLicensingAgency } from '../hooks/licensing-agency-provider'

interface LicensingAgencyNavigationProps {
  licensingAgency: LicensingAgency
}

interface NavigationItem {
  label: string
  to: RouteKey
  feature?: Feature
}

export const LicensingAgencyNavigationItems = [
  {
    label: 'Companies',
    to: '/licensing-agencies/:agencyId/companies',
  },
  {
    label: 'Users',
    to: '/licensing-agencies/:agencyId/users',
    feature: LicensingAgentsFeature.Users,
  },
  {
    label: 'History',
    to: '/licensing-agencies/:agencyId/history',
    feature: LicensingAgentsFeature.History,
  },
] satisfies readonly NavigationItem[]

export default function LicensingAgencyNavigation({
  licensingAgency: { id: agencyId },
}: LicensingAgencyNavigationProps) {
  const { user } = useAuth()
  const { licensingAgencyUser } = useLicensingAgency()

  if (!agencyId) return null

  // Build eligible navigation items
  let navigation = LicensingAgencyNavigationItems.filter(
    ({ feature }) => !feature || feature.isEnabled(user)
  )

  // If the active user is a user on this agency, ensure they
  // have the owner role on this agency *specifically*
  if (
    licensingAgencyUser &&
    licensingAgencyUser.role !== MccsRoles.LICENSING_AGENCY_OWNER
  )
    navigation = navigation.filter(
      ({ feature }) => feature !== LicensingAgentsFeature.Users
    )

  return (
    <div className="bg-primary text-primary-foreground rounded-md p-4">
      <ScrollArea className="-mb-2 w-full pb-1 whitespace-nowrap">
        <NavigationMenu>
          {navigation.length === 1 ? (
            <div className="pb-1">
              <span className="decoration-primary-foreground whitespace-nowrap underline decoration-2 underline-offset-6">
                {navigation[0].label}
              </span>
            </div>
          ) : (
            <NavigationMenuList className="flex gap-4 lg:gap-6">
              {navigation.map(({ label, to, feature }) =>
                feature ? (
                  <HasFeature key={to} feature={feature}>
                    <LicensingAgencyNavigation.Item
                      to={buildPath(to, { agencyId })}
                    >
                      {label}
                    </LicensingAgencyNavigation.Item>
                  </HasFeature>
                ) : (
                  <LicensingAgencyNavigation.Item
                    key={to}
                    to={buildPath(to, { agencyId })}
                  >
                    {label}
                  </LicensingAgencyNavigation.Item>
                )
              )}
            </NavigationMenuList>
          )}
        </NavigationMenu>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  )
}

LicensingAgencyNavigation.Item = function LicensingAgencyNavigationItem({
  to,
  children,
}: PropsWithChildren<{ to: To }>) {
  return (
    // Bottom padding to account for scrollbar height to show underline decoration
    <NavigationMenuItem className="pb-1">
      <ReactRouterNavLink
        to={to}
        className={({ isActive }) =>
          cn(
            'hover:decoration-primary-foreground whitespace-nowrap underline decoration-transparent decoration-2 underline-offset-6 transition-colors duration-200',
            isActive ? 'decoration-primary-foreground' : null
          )
        }
      >
        {children}
      </ReactRouterNavLink>
    </NavigationMenuItem>
  )
}
